/**
 * The 72 px icon rail (spec M0-F4 §7.3).
 *
 * monday's rail also carries Sidekick / Agents / Vibe / Workflows / Notetaker — AI products we
 * are not building (§3.2), so those slots are simply absent and the rail is three items plus
 * the collapse control. Rail items are `<button>`s with an accessible name inside a `<nav>`.
 */
import { Fragment } from 'react';
import { Icon, IconButton, Tooltip, icons } from '@elizmonday/ui';
import type { ShellCapabilities } from '../../modules/shell/bootstrap';
import type { RailSlot } from '../../modules/shell/store';
import { TasksRailEntry } from '../../modules/tasks/TasksRailEntry';

export interface IconRailProps {
  slot: RailSlot;
  onSelect: (slot: RailSlot) => void;
  /** `true` while the pane is collapsed; the rail then shows the `»` re-expand control (S3). */
  collapsed: boolean;
  onExpand: () => void;
  capabilities: ShellCapabilities;
  /**
   * B10: monday's **More** rail entry opens a 220 px menu (`[c:rail-more]`), it does not swap
   * the pane. The rail reports where to anchor it; the sidebar owns the menu.
   */
  onOpenMore?: (x: number, y: number) => void;
}

interface RailItem {
  slot: RailSlot;
  label: string;
  icon: (typeof icons)[keyof typeof icons];
  visible: boolean;
}

export function IconRail({ slot, onSelect, collapsed, onExpand, capabilities, onOpenMore }: IconRailProps) {
  const items: RailItem[] = [
    { slot: 'workspace', label: 'Workspace', icon: icons.workspace, visible: capabilities.workspaceTree },
    { slot: 'favorites', label: 'Favorites', icon: icons.favorite, visible: capabilities.favorites },
    { slot: 'more', label: 'More', icon: icons.moreActions, visible: true },
  ];

  const visible = items.filter((item) => item.visible);
  /**
   * **M10.** The Tasks entry sits **between Workspace and Favorites** (the user's decision of
   * 2026-09-15, `docs/13-tasks-section-proposal.md`). It is placed before the first entry that
   * is not *Workspace* rather than at a fixed index, so an account whose capabilities hide the
   * workspace tree still gets it in the same relative place, and an account with neither
   * *Favorites* nor *More* gets it at the end instead of losing it.
   */
  const tasksAt = (() => {
    const at = visible.findIndex((item) => item.slot !== 'workspace');
    return at < 0 ? visible.length : at;
  })();

  return (
    <nav className="ez-rail" aria-label="Sections" data-testid="sidebar-rail">
      {collapsed ? (
        <span className="ez-rail__expand">
          <Tooltip content="Expand the sidebar">
            <IconButton
              icon={icons.expandRight}
              ariaLabel="Expand the sidebar"
              kind="tertiary"
              onClick={onExpand}
              data-testid="rail-expand"
            />
          </Tooltip>
        </span>
      ) : null}

      {/*
        M5.C3: monday's rail carries a **My work** entry (`side-nav-v2-more-selected`). It is a
        *link*, not a pane slot — clicking it navigates to `/my_work` rather than swapping the
        sidebar's content — so it is rendered as an `<a>` and needs no new `RailSlot`.
      */}
      <a className="ez-rail__item" href="/my_work" data-testid="rail-my-work">
        <span className="ez-rail__tile">
          <Icon icon={icons.home} size={20} ignoreFocusStyle />
        </span>
        <span className="ez-rail__label">My work</span>
      </a>

      {visible.map((item, index) => (
        <Fragment key={item.slot}>
          {index === tasksAt ? <TasksRailEntry /> : null}
          <button
            type="button"
            className="ez-rail__item"
            aria-current={slot === item.slot && !collapsed}
            aria-pressed={slot === item.slot && !collapsed}
            onClick={(event) => {
              if (item.slot === 'more' && onOpenMore) {
                const box = event.currentTarget.getBoundingClientRect();
                onOpenMore(box.right + 4, box.top);
                return;
              }
              onSelect(item.slot);
            }}
            data-testid={`rail-${item.slot}`}
          >
            <span className="ez-rail__tile">
              <Icon icon={item.icon} size={20} ignoreFocusStyle />
            </span>
            <span className="ez-rail__label">{item.label}</span>
          </button>
        </Fragment>
      ))}
      {tasksAt === visible.length ? <TasksRailEntry /> : null}
    </nav>
  );
}
